import { Component, inject, model, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { DateTime } from 'luxon';
import { MaskService } from '../../../services/mask.service';
import { MaskitoDirective } from '@maskito/angular';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-date-calc-dialog',
  template: ` <div mat-dialog-header><h3>Calculate Age</h3></div>
    <div mat-dialog-content>
      <mat-form-field>
        <mat-label>Select a date</mat-label>
        <input
          matInput
          [formControl]="date"
          [matDatepicker]="picker"
          [maskito]="dateMask"
        />
        <mat-hint>MM/DD/YYYY</mat-hint>

        <mat-datepicker-toggle matIconSuffix [for]="picker" />
        <mat-datepicker #picker />
      </mat-form-field>
    </div>
    <mat-dialog-actions align="end">
      <button matButton mat-dialog-close>CANCEL</button>
      <button matButton cdkFocusInitial (click)="closeDialog()">OK</button>
    </mat-dialog-actions>`,
  imports: [
    MatDatepickerModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MaskitoDirective,
  ],
  providers: [],
})
export class DateCalcDialog {
  dialogRef = inject<MatDialogRef<DateCalcDialog>>(
    MatDialogRef<DateCalcDialog>
  );
  maskSvc = inject(MaskService);
  data = inject(MAT_DIALOG_DATA);
  formControl = new FormControl();
  date = new FormControl();
  dateMask = this.maskSvc.dateMaskSpecs();

  constructor() {}

  closeDialog() {
    this.dateChange();
    this.dialogRef.close({
      years: this.formControl.value,
    });
  }

  dateChange() {
    if (this.date.value) {
      const selectedDate = DateTime.fromJSDate(this.date.value);
      const todaysDate = DateTime.fromJSDate(new Date());
      const yearsDiff = Math.floor(
        todaysDate.diff(selectedDate, 'years').years
      );
      if (yearsDiff > -1) {
        this.formControl.setValue(yearsDiff);
      }
    }
    this.date.setValue(null);
  }
}
@Component({
  selector: 'app-form-date-calc',
  styleUrls: ['form-dateCalc.component.scss'],
  template: `
    <div>
      <div class="date-calc-age">
        <mat-form-field class="w-100">
          <mat-label>{{ to.placeholder }}</mat-label>
          <input
            attr.maxlength="{{ to.maxLength ? to.maxLength : '' }}"
            matInput
            [placeholder]="props.placeholder"
            [formControl]="formControl"
            [required]="props.required"
            (keypress)="
              $event.charCode < 48 || $event.charCode > 57
                ? $event.preventDefault()
                : null
            "
          />
          @if (
            formControl.hasError('required') &&
            !formControl.hasError('regRequired')
          ) {
            <mat-error>This field is required.</mat-error>
          }
          @if (formControl.hasError('regRequired')) {
            <mat-error>
              This field is needed for suitability review. You can opt out below
              if the applicant refuses to provide the information.
            </mat-error>
          }
        </mat-form-field>
      </div>
      <div class="date-calc-date">
        <button
          matButton="elevated"
          color="accent"
          type="button"
          [disabled]="formControl.disabled"
          (click)="openDialog()"
        >
          <mat-icon>today</mat-icon>Calculate Age
        </button>
      </div>
    </div>
  `,
  imports: [
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    MatButtonModule,
  ],
})
export class FormlyCustomDateCalcComponent extends FieldType<FieldTypeConfig> {
  dialog = inject(MatDialog);

  constructor() {
    super();
  }

  openDialog() {
    const dialogRef = this.dialog.open(DateCalcDialog, {});

    dialogRef.afterClosed().subscribe(result => {
      if (result?.years) this.formControl.setValue(result.years);
    });
  }
}
